import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@app/portal-sdk";

export type ApiQueryState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-run the query. Keeps the previous data while reloading. */
  reload: () => Promise<void>;
  /** Replace the cached data locally (after a mutation) without refetching. */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
};

export const errorMessage = (err: unknown): string => {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
};

/**
 * Minimal data-fetching hook. `fn` is re-run whenever `deps` change.
 * Results from stale runs are ignored.
 */
export const useApiQuery = <T,>(
  fn: () => Promise<T>,
  deps: React.DependencyList
): ApiQueryState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const runId = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const reload = useCallback(async () => {
    const id = ++runId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fnRef.current();
      if (id === runId.current) setData(result);
    } catch (err) {
      if (id === runId.current) setError(errorMessage(err));
    } finally {
      if (id === runId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload, setData };
};
